import { Fragment, memo, useMemo, useState } from 'react';
import {
  DataTable,
  SkeletonText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent
} from '@carbon/react';
import cx from 'clsx';
import { keyBy } from 'lodash';

import { useIsReadonly } from 'core/readonly';
import useBusinessUnitsQuery from 'shared/api/business-units/useBusinessUnitsQuery';
import ActionButton from 'shared/components/ActionButton';
import TableExpandText from 'shared/components/data-table/TableExpandText';
import TableToolbarCell from 'shared/components/data-table/TableToolbarCell';
import SiteSpecificSpaceModal from 'shared/site-specific-spaces/SiteSpecificSpaceModal';
import { formatArea, formatNumeric } from 'utils/format';
import styles from 'theme/features/shared/site-specific-spaces.module.scss';

const SiteSpecificSpacesTable = ({
  spaces,
  onAddSpace,
  onEditSpace,
  onRemoveSpace,
  withHeader = false,
  measurementSystem,
  isExport = false
}) => {
  const { data: businessUnits, isLoading: isLoadingBusinessUnits } = useBusinessUnitsQuery();
  const businessUnitsById = useMemo(() => keyBy(businessUnits, 'id'), [businessUnits]);

  const isReadonly = useIsReadonly() || isExport;

  const tableData = spaces?.map((x) => ({
    id: x.id.toString(),
    name: x.name,
    area: formatArea(x.area, measurementSystem),
    quantity: formatNumeric(x.quantity),
    totalArea: formatArea(x.area * x.quantity, measurementSystem),
    businessUnit: isLoadingBusinessUnits ? (
      <SkeletonText className={styles['business-unit-skeleton']} width="110px" />
    ) : (
      businessUnitsById[x.businessUnitId]?.name ?? 'Non-BU Specific'
    ),
    notes: x.notes
  }));

  const headers = [
    { key: 'name', header: 'Space Name' },
    { key: 'area', header: 'Area' },
    { key: 'quantity', header: 'Quantity' },
    { key: 'totalArea', header: 'Total Area' },
    { key: 'businessUnit', header: 'Business Unit' },
    { key: 'notes', header: 'Notes' }
  ];

  return (
    <DataTable
      headers={headers}
      rows={tableData}
      size="sm"
      render={({ rows, getTableContainerProps, getToolbarProps, getTableProps, getHeaderProps, getRowProps }) => (
        <TableContainer
          title="Site-Specific Spaces"
          description="Site-specific spaces are any special or unique space types that are beyond the typical spread"
          className={cx(styles['table-header'], !withHeader && styles['table-header--hidden'])}
          {...getTableContainerProps()}
        >
          {!isExport && (
            <TableToolbar {...getToolbarProps()} size="sm">
              {!isReadonly && (
                <TableToolbarContent>
                  <ActionButton onClick={onAddSpace}>Add a Space</ActionButton>
                </TableToolbarContent>
              )}
            </TableToolbar>
          )}
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                <TableExpandHeader id="expand" />
                {headers.map((header) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
                {!isReadonly && <TableToolbarCell.Header />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const notes = spaces[index]?.notes;

                const actionsCellNode = !isReadonly && (
                  <TableToolbarCell>
                    <TableToolbarCell.MenuItem itemText="Edit" onClick={() => onEditSpace(index)} />
                    <TableToolbarCell.MenuItem isDelete itemText="Delete" onClick={() => onRemoveSpace(index)} />
                  </TableToolbarCell>
                );

                return (
                  <Fragment key={row.id}>
                    {notes ? (
                      <>
                        <TableExpandRow
                          expandHeader="expand"
                          {...getRowProps({ row })}
                          {...(isExport && { isExpanded: true })}
                        >
                          {row.cells.map((cell) => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                          {actionsCellNode}
                        </TableExpandRow>
                        <TableExpandedRow colSpan={headers.length + 2}>
                          <TableExpandText>{notes}</TableExpandText>
                        </TableExpandedRow>
                      </>
                    ) : (
                      <TableRow {...getRowProps({ row })} key={row.id}>
                        <TableCell key="empty-expand" />
                        {row.cells.map((cell) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                        {actionsCellNode}
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    />
  );
};

const SiteSpecificSpaces = ({
  withHeader = false,
  spaces,
  onAddSpace,
  onEditSpace,
  onRemoveSpace,
  measurementSystem,
  isExport = false
}) => {
  const [isSpaceModalOpen, setIsSpaceModalOpen] = useState(false);
  const [spaceEditIndex, setSpaceEditIndex] = useState(null);

  return (
    <>
      <SiteSpecificSpaceModal
        measurementSystem={measurementSystem}
        open={isSpaceModalOpen}
        siteSpecificSpace={spaceEditIndex === null ? null : spaces[spaceEditIndex]}
        onClose={() => setIsSpaceModalOpen(false)}
        onSubmit={(value) => (spaceEditIndex === null ? onAddSpace(value) : onEditSpace(spaceEditIndex, value))}
      />
      <SiteSpecificSpacesTable
        measurementSystem={measurementSystem}
        withHeader={withHeader}
        spaces={spaces}
        onAddSpace={() => {
          setIsSpaceModalOpen(true);
          setSpaceEditIndex(null);
        }}
        onEditSpace={(index) => {
          setIsSpaceModalOpen(true);
          setSpaceEditIndex(index);
        }}
        onRemoveSpace={onRemoveSpace}
        isExport={isExport}
      />
    </>
  );
};

export default memo(SiteSpecificSpaces);
